import cloneDeep from 'lodash.clonedeep';
import {
  FormTemplate,
  FormTemplateSection,
  FormTemplateOption
} from 'data/template/template.types';
import {
  SupportedFeaturesAccessFunctions
} from 'data/supportedFeatures/supportedFeatures.types';
import {
  NetworkElementProps,
  NetworkTopologyMapData,
  XcipioNodesTopology
} from 'data/network-topology/network-topology.types';
import { CollectionFunction } from 'data/collectionFunction/collectionFunction.types';

// eslint-disable-next-line import/prefer-default-export
export const adaptFormTemplateOptions = (
  template: FormTemplate,
  originalNetworkTopology: NetworkTopologyMapData,
  locations: string[],
  availableAccessFunction: SupportedFeaturesAccessFunctions,
  collectionFunctions: CollectionFunction[],
  stringSearchHistory: FormTemplateOption[],
): FormTemplate => {
  const clonedTemplate = cloneDeep(template);

  const locationOptions = getFormTemplateOptions(locations);
  const poiTypeOptions = getPoiTypesOptions(originalNetworkTopology.accessFunctions, availableAccessFunction);
  const poiVendorsOptions = getPoiVendorsOptions(originalNetworkTopology.accessFunctions, availableAccessFunction);
  const xcipioServiceOptions = getXcipioServiceOptions(originalNetworkTopology.xcipioNodes, originalNetworkTopology.ccpags, originalNetworkTopology.mdf2s, originalNetworkTopology.mdf3s, originalNetworkTopology.admfs);
  const cfTypeOptions = getCfTypeOptions(collectionFunctions);

  adaptTemplateForSearchSuggest(clonedTemplate, stringSearchHistory)
  setFormTemplateOptions(clonedTemplate, 'location', locationOptions);
  setFormTemplateOptions(clonedTemplate, 'poiType', poiTypeOptions);
  setFormTemplateOptions(clonedTemplate, 'poiVendor', poiVendorsOptions);
  setFormTemplateOptions(clonedTemplate, 'xcipioService', xcipioServiceOptions);
  setFormTemplateOptions(clonedTemplate, 'cfType', cfTypeOptions);
  return clonedTemplate;
};

const adaptTemplateForSearchSuggest = (
  template: FormTemplate,
  stringSearchHistory: any,
  ): void => {
    (template.main as FormTemplateSection).fields.search.options=stringSearchHistory
};

const getFormTemplateOptions = (
  fieldOptions: string[]
): FormTemplateOption[] => {
  if (!fieldOptions?.length) {
    return [];
  }
  return Array.from(new Set(fieldOptions))
    .sort()
    .map((item) => ({
      value: item,
      label: item
    }));
};

const getPoiTypes = (poiList: NetworkElementProps[]) => {
  const types = poiList.map((e) => e.data.tag)
  return Array.from(new Set(types));
};

const getPoiOntologyOptions = (
  poiData: any[],
  field: string,
  supportedFeatures: SupportedFeaturesAccessFunctions
): FormTemplateOption[] => {

  const typeOptionsValues = getPoiTypes(poiData)
    .map((type) => {
      const poi = (supportedFeatures as any)[type];
      if (!poi) {
        return undefined;
      }
      return poi[field];
    })
    .filter((type) => !!type)
    .sort();

    return Array.from(new Set(typeOptionsValues))
      .map((type) => ({
        label: type,
        value: type
      }));
};

const getPoiTypesOptions = (
  xcipioNodesTopology: XcipioNodesTopology[],
  availableAccessFunction: SupportedFeaturesAccessFunctions
): FormTemplateOption[] => {
  const optionsMap: { [key: string]: string } = {
    'SDM(HSS)': 'HSS (SDM)',
    UDM: 'UDM (SDM)'
  }

  return getPoiOntologyOptions(
    xcipioNodesTopology,
    'type',
    availableAccessFunction
  ).map((option) => ({
    ...option,
    label: optionsMap[option.label] ?? option.label,
  }));
};

const getPoiVendorsOptions = (
  xcipioNodesTopology: XcipioNodesTopology[],
  availableAccessFunction: SupportedFeaturesAccessFunctions
): FormTemplateOption[] => {

  return getPoiOntologyOptions(
    xcipioNodesTopology,
    'vendor',
    availableAccessFunction
  );
};

const getXcipioServiceOptions = (
  xcipioNodesTopology: XcipioNodesTopology[] = [],
  ccpagXcipioNodesTopology: XcipioNodesTopology[] = [],
  mdf2XcipioNodesTopology: XcipioNodesTopology[] = [],
  mdf3XcipioNodesTopology: XcipioNodesTopology[] = [],
  admfXcipioNodesTopology: XcipioNodesTopology[] = [],
): FormTemplateOption[] => {
  const nodeTypes: string[] = [...xcipioNodesTopology, ...ccpagXcipioNodesTopology, ...mdf2XcipioNodesTopology, ...mdf3XcipioNodesTopology, ...admfXcipioNodesTopology].map((node) => {
    return node.data.nodeType;
  });
  return Array.from(new Set(nodeTypes).values()).map((type) => ({
    label: type,
    value: type
  }));
};

const getCfTypeOptions = (
  collectionFunctions: CollectionFunction[],
): FormTemplateOption[] => {
  const cfTypes = collectionFunctions
    .map((cf) => cf.type);

  return Array.from(new Set(cfTypes).values()).map((type) => ({
    label: type,
    value: type
  }));
};

const setFormTemplateOptions = (
  template: FormTemplate,
  fieldName: string,
  fieldOptions: FormTemplateOption[],
): void => {
  const placeholderOption = ((template.main as FormTemplateSection).fields[
    fieldName
  ].options as FormTemplateOption[])[0];

  template.key = Date.now().toString();
  (template.main as FormTemplateSection).fields[fieldName].options = [
    placeholderOption,
    ...fieldOptions
  ];
};
